import { AddCircleOutline, SearchOutlined, Download as DownloadIcon } from '@mui/icons-material'
import { Box, Stack, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import InputAdornment from '@mui/material/InputAdornment'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import React, { KeyboardEvent, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PermissionAccess, PermissionKey, PermissionProvider } from '../../../../context'
import { Format, dateFormat } from '../../../../utility/date-format'
import { PATH } from '../../../../utility/path'
import { Preloader } from '../../../common'
import { ArchiveButton, CustomPagination, CustomTableCell, EditButton, ViewButton } from '../../../shared'
import { TADMembership } from '../../../../type'
import { EntitiesTypes, getEntityTitle } from '../../../../view/admin/ad-management/EntitiesTabs'
import { ConfirmationAlert } from '../../../shared/alerts'
import { API } from '../../../../api'
import { usePermission } from '../../../../context/Permission'

type IEntitiesListProps = {
  entityType: EntitiesTypes
  entities: TADMembership[]
  setSearch: (search: string) => void
  setEntity: (sharedGroup: string) => void
  totalCount: number
  page: number
  maxPageSize: number
  setPage: (page: number) => void
  setIsEntitiesView: (isEntitiesView: boolean) => void
  isLoading: boolean
  search: string
  handleExportEntitiesButtonClick: () => void
  getEntities?: () => Promise<void> | void
}

export const EntitiesList: React.FC<IEntitiesListProps> = ({
  entityType,
  entities,
  setSearch,
  setEntity,
  totalCount,
  page,
  maxPageSize,
  setPage,
  setIsEntitiesView,
  isLoading,
  search,
  handleExportEntitiesButtonClick,
  getEntities,
}) => {
  const titleName = getEntityTitle(entityType)
  const navigation = useNavigate()
  const [inputValue, setInputValue] = useState(search)
  const [isLoadingArchive, setIsLoadingArchive] = useState(false)
  const [isConfirmModal, setIsConfirmModal] = useState(false)
  const [archiveEntity, setArchiveEntity] = useState('')
  const hasDetailsPermission = usePermission(PermissionKey.btns_users)

  useEffect(() => {
    setInputValue(search)
  }, [search])

  const renderPreloader = () => (
    <Grid
      container
      justifyContent='center'
    >
      <Preloader size='sm' />
    </Grid>
  )

  function getEntityColumnTitles(entityType: EntitiesTypes): { id: string; name: string } {
    switch (entityType) {
      case EntitiesTypes.pharmacies:
        return { id: 'Pharmacy ID', name: 'Pharmacy Name' }
      case EntitiesTypes.distributors:
        return { id: 'Distributor ID', name: 'Distributor Name' }
      case EntitiesTypes.referral_types:
        return { id: 'Referral Type', name: 'Description' }
      case EntitiesTypes.omit_measures:
        return { id: 'Measure Name', name: 'Description' }
      case EntitiesTypes.reports:
        return { id: 'Report name', name: 'Description' }
      default:
        return { id: `${getEntityTitle(entityType)} ID`, name: `${getEntityTitle(entityType)} Name` }
    }
  }
  const { id: idTitle, name: nameTitle } = getEntityColumnTitles(entityType)

  const getDeleteType = (type: EntitiesTypes) => {
    switch (type) {
      case EntitiesTypes.pharmacies:
        return 'pharmacies'
      case EntitiesTypes.distributors:
        return 'distributors'
      case EntitiesTypes.referral_types:
        return 'referral_types'
      case EntitiesTypes.omit_measures:
        return 'omissions'
      case EntitiesTypes.reports:
        return 'qlik_based_reports'
      default:
        return ''
    }
  }

  const handleArchiveEntity = async () => {
    try {
      setIsLoadingArchive(true)
      await API.ADManagement.entities.delete(archiveEntity || '', getDeleteType(entityType))
      setIsLoadingArchive(false)
      setIsConfirmModal(false)
      await getEntities?.()
    } catch (err) {
      console.log('Error archiving entity:', err)
    } finally {
      setArchiveEntity('')
    }
  }

  const renderToolbar = () => {
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setPage(1)
        setSearch(inputValue)
      }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
    }
    const handleCreateSharedType = () => {
      navigation(`${PATH.admin.ad_management.entities}/new?type=${entityType}`)
    }

    return (
      <Grid
        container
        spacing={2}
        sx={{ pb: 3 }}
      >
        <Grid
          item
          xs={4}
        >
          <TextField
            label='Search'
            size='small'
            fullWidth
            value={inputValue}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined />
                </InputAdornment>
              ),
            }}
            inputProps={{
              'data-testid': `${entityType}-search`,
            }}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
          />
        </Grid>
        <Grid
          container
          item
          xs={2}
          justifyContent='flex-start'
        >
          <Button
            variant='outlined'
            startIcon={<DownloadIcon />}
            onClick={handleExportEntitiesButtonClick}
            data-testid={`${entityType}-export`}
            className={'h-full'}
            disabled={entities.length === 0 || isLoading}
          >
            Export
          </Button>
        </Grid>
        <Grid
          container
          item
          xs={6}
          justifyContent='flex-end'
        >
          <PermissionProvider permission={PermissionKey.btns_entities}>
            <Button
              variant='contained'
              startIcon={<AddCircleOutline />}
              onClick={handleCreateSharedType}
              data-testid={`${entityType}-create`}
              className={'h-full'}
            >
              Create {titleName}
            </Button>
          </PermissionProvider>
        </Grid>
      </Grid>
    )
  }

  const HeaderCell = ({ children, className }: { children: React.ReactNode; className?: string }) => (
    <TableCell className={`no-wrap ${className}`}>
      <span
        className='t-w-200'
        style={{ fontWeight: 'bold' }}
      >
        {children}
      </span>
    </TableCell>
  )

  const renderEntities = () => {
    const renderHead = () => (
      <TableHead>
        <TableRow className='bg-base300'>
          <HeaderCell>{idTitle}</HeaderCell>
          <HeaderCell>{nameTitle}</HeaderCell>
          <HeaderCell>Updated At</HeaderCell>
          <HeaderCell> </HeaderCell>
        </TableRow>
      </TableHead>
    )

    const renderBody = () => {
      const handleEdit = (entity: string) => {
        navigation(`${PATH.admin.ad_management.entities}/${entity}?type=${entityType}`)
      }

      const handleView = (entity: string) => {
        setIsEntitiesView(true)
        setEntity(entity)
      }
      const handleArchive = (entity: TADMembership) => {
        setArchiveEntity(entity.GroupName)
        setIsConfirmModal(true)
      }
      return (
        <TableBody>
          {entities.map((entity, index) => (
            <TableRow key={entity.AdGroupId}>
              <CustomTableCell
                dataTest={`entity-${index}-${entityType}-id`}
                sx={{ minWidth: '200px' }}
              >
                {entity.GroupName}
              </CustomTableCell>
              <CustomTableCell
                dataTest={`entity-${index}-${entityType}-name`}
                sx={{ minWidth: '200px' }}
              >
                {entity.Description}
              </CustomTableCell>

              <CustomTableCell
                dataTest={`entity-${index}-${entityType}-date`}
                sx={{ minWidth: '200px' }}
              >
                {dateFormat(entity.WhenChanged, Format['DD MMM YYYY'])} at {dateFormat(entity.WhenChanged, Format['HH MM'])}
              </CustomTableCell>
              <CustomTableCell
                dataTest={`entity-${index}-${entityType}-date`}
                sx={{ minWidth: '50px' }}
              >
                <Stack
                  direction={'row'}
                  spacing={1}
                  sx={{ width: '100%', height: '100%' }}
                  justifyContent={'end'}
                >
                  <ViewButton
                    onClick={() => handleView(entity.GroupName)}
                    dataTestId={`entity--${index}-${entityType}-view`}
                  />
                  <PermissionProvider
                    permission={PermissionKey.btns_entities}
                    className='d-ib'
                  >
                    <EditButton
                      onClick={() => handleEdit(entity.GroupName)}
                      dataTestId={`entity-${index}-${entityType}-edit`}
                    />
                  </PermissionProvider>
                  <PermissionProvider
                    permission={PermissionKey.btns_entities}
                    className='d-ib'
                  >
                    <ArchiveButton
                      onClick={() => handleArchive(entity)}
                      dataTestId={`entity-${index}-${entityType}-archive`}
                      disabled={hasDetailsPermission === PermissionAccess?.Read}
                    />
                  </PermissionProvider>
                </Stack>
              </CustomTableCell>
            </TableRow>
          ))}
        </TableBody>
      )
    }

    const renderPagination = () => {
      const handlePagination = (event: React.ChangeEvent<unknown>, page_number: number) => {
        setPage(page_number)
      }

      return (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          mt={4}
        >
          <CustomPagination
            total_count={totalCount}
            max_page_size={maxPageSize}
            page_number={page}
            showFirstButton={false}
            showLastButton={false}
            handlePagination={handlePagination}
          />
        </Box>
      )
    }

    if (entities && entities.length > 0) {
      return (
        <div className='table-wrapper'>
          <div style={{ display: 'flex' }}>
            <TableContainer
              className='table-container main-table'
              style={{ flex: 4 }}
            >
              <Table>
                {renderHead()}
                {renderBody()}
              </Table>
            </TableContainer>
          </div>
          <div className='pagination-container'>{renderPagination()}</div>
        </div>
      )
    } else {
      return <div style={{ textAlign: 'center', fontWeight: 'bold', color: 'var(--pantoneBlue)' }}>No Results</div>
    }
  }

  return (
    <>
      {renderToolbar()}
      {isLoading ? renderPreloader() : renderEntities()}
      <ConfirmationAlert
        isShow={isConfirmModal}
        title={
          <Typography
            fontWeight={'bold'}
            variant={'h6'}
            fontSize={'20px'}
            data-testid={'entity-action'}
          >
            Are you sure you want to delete this AD Group?
          </Typography>
        }
        content={
          <Typography data-testid={'entity-action'}>
            <strong>Please Note!</strong> Deleting this Active directory group will permanently remove all permissions and access rights
            granted through it
          </Typography>
        }
        dataTestId='confirmation'
        onClose={() => {
          setIsConfirmModal(false)
          setArchiveEntity('')
        }}
        onCancel={() => {
          setIsConfirmModal(false)
          setArchiveEntity('')
        }}
        onConfirm={handleArchiveEntity}
        cancellationLabel='Cancel'
        confirmationLabel='Confirm'
        isLoading={isLoadingArchive}
        shouldAlignBetween
      />
    </>
  )
}
